import { useState, useCallback, useEffect, useMemo } from 'react'
import { Gift, Plus } from 'lucide-react'
import { PageShell } from '@/components/common/PageShell'
import {
  useWorkspaceCompanyFilter,
  WorkspaceCompanyChips,
} from '@/features/companies/components/WorkspaceCompanyFilter'
import { useBonusesWorkspace } from '@/features/bonuses/hooks/useBonuses'
import { TablePaginationBar } from '@/components/common/TablePaginationBar'
import {
  WorkspaceToolbar,
  WorkspaceToolbarSearch,
} from '@/components/common/WorkspaceToolbar'
import { Button } from '@/components/ui'
import { usePermissions } from '@/hooks/usePermissions'
import type {
  BonusEntry,
  MyBonusListQuery,
} from '@/features/bonuses/types/bonus.types'
import { BonusEntryTable } from './BonusEntryTable'
import { BonusDetailModal } from './BonusDetailModal'
import { CreateBonusModal } from './CreateBonusModal'

const PAGE_SIZE_OPTIONS = [10, 20, 50] as const

export function WorkspaceBonusesPage(): React.ReactElement {
  const { can } = usePermissions()
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState<number>(10)
  const [status, setStatus] = useState<string>('')
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const filter = useWorkspaceCompanyFilter({ onChange: () => setPage(1) })
  const [selectedEntry, setSelectedEntry] = useState<BonusEntry | null>(null)
  const [createOpen, setCreateOpen] = useState(false)

  const query = useMemo<MyBonusListQuery>(
    () => ({
      page,
      limit,
      status: status ? (status as BonusEntry['status']) : undefined,
      search: debouncedSearch || undefined,
      companyIds: filter.companyIds,
    }),
    [page, limit, status, debouncedSearch, filter.companyIds]
  )

  const listQuery = useBonusesWorkspace(query)

  const rows = useMemo(() => listQuery.data?.data ?? [], [listQuery.data?.data])
  const total = listQuery.data?.pagination?.total ?? 0
  const totalPages = Math.max(1, Math.ceil(total / limit))
  const canCreateBonus = can('bonuses', 'create')
  const selectedCompanyIds = Array.from(filter.selectedCompanyIds)
  const createCompanyId =
    selectedCompanyIds.length === 1
      ? selectedCompanyIds[0]
      : filter.companies.length === 1
        ? filter.companies[0]._id
        : null

  const handleStatusChange = useCallback((s: string) => {
    setStatus(s)
    setPage(1)
  }, [])

  useEffect(() => {
    const id = window.setTimeout(() => {
      setDebouncedSearch(search.trim())
      setPage(1)
    }, 300)
    return () => window.clearTimeout(id)
  }, [search])

  const handleRowClick = useCallback(
    (entryId: string) => {
      const found = rows.find((r) => r._id === entryId)
      if (found) setSelectedEntry(found)
    },
    [rows]
  )

  return (
    <>
      <PageShell
        breadcrumbItems={[
          { label: 'Payroll', to: '/payroll' },
          { label: 'Bonuses' },
        ]}
        icon={Gift}
        title="Bonuses"
        description="Cross-company bonus records across every company you can read."
        className="gap-0"
        hideHeader
      >
        <div className="space-y-4">
          <WorkspaceToolbar
            filter={<WorkspaceCompanyChips filter={filter} />}
            actions={
              canCreateBonus ? (
                <Button
                  type="button"
                  variant="primary"
                  size="sm"
                  onClick={() => setCreateOpen(true)}
                  disabled={!createCompanyId}
                  title={createCompanyId ? 'Issue bonus' : 'Select one company first'}
                >
                  <Plus className="h-4 w-4" aria-hidden />
                  <span className="ml-1.5">Issue bonus</span>
                </Button>
              ) : null
            }
          >
            <WorkspaceToolbarSearch
              value={search}
              onChange={setSearch}
              placeholder="Search name or email…"
              ariaLabel="Search bonuses"
              active={Boolean(debouncedSearch)}
            />
          </WorkspaceToolbar>

          <BonusEntryTable
            entries={rows}
            isLoading={listQuery.isLoading}
            error={listQuery.error as Error | null}
            statusFilter={status}
            onStatusFilterChange={handleStatusChange}
            onRowClick={handleRowClick}
            onRetry={() => listQuery.refetch()}
            showCompany
            showEmployee
            companyNameById={filter.companyNameById}
            emptyMessage="No bonus records in this filter."
          />

          <TablePaginationBar
            page={page}
            limit={limit}
            total={total}
            totalPages={totalPages}
            onPageChange={setPage}
            onLimitChange={setLimit}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
          />
        </div>
      </PageShell>

      <BonusDetailModal
        companyId={selectedEntry?.companyId ?? ''}
        entry={selectedEntry}
        onClose={() => setSelectedEntry(null)}
      />

      {createCompanyId && (
        <CreateBonusModal
          companyId={createCompanyId}
          open={createOpen}
          onClose={() => setCreateOpen(false)}
        />
      )}
    </>
  )
}

WorkspaceBonusesPage.displayName = 'WorkspaceBonusesPage'
